import { create } from 'zustand';
import type { Cart } from '@/types';
import { cartService } from '@/services';

interface CartState {
  cart: Cart | null;
  isLoading: boolean;
  error: string | null;
  fetchCart: () => Promise<void>;
  addProduct: (productId: string, quantity: number) => Promise<void>;
  addTcg: (masterCardId: string, tcgListingId: string, quantity: number) => Promise<void>;
  updateItem: (itemId: string, quantity: number) => Promise<void>;
  removeItem: (itemId: string) => Promise<void>;
  clearCart: () => Promise<void>;
  setCart: (cart: Cart | null) => void;
  clearError: () => void;
}

export const useCartStore = create<CartState>((set, get) => ({
  cart: null,
  isLoading: false,
  error: null,

  fetchCart: async () => {
    set({ isLoading: true, error: null });
    try {
      const cart = await cartService.getCart();
      set({ cart, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch cart';
      set({ error: message, isLoading: false, cart: null });
    }
  },

  addProduct: async (productId: string, quantity: number) => {
    set({ isLoading: true, error: null });
    try {
      await cartService.addProduct({ productId, quantity });
      // Refresh cart after adding
      await get().fetchCart();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to add product to cart';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  addTcg: async (masterCardId: string, tcgListingId: string, quantity: number) => {
    set({ isLoading: true, error: null });
    try {
      await cartService.addTcg({ masterCardId, tcgListingId, quantity });
      // Refresh cart after adding
      await get().fetchCart();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to add TCG item to cart';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updateItem: async (itemId: string, quantity: number) => {
    set({ isLoading: true, error: null });
    try {
      await cartService.updateItem(itemId, { quantity });
      // Refresh cart after updating
      await get().fetchCart();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update cart item';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  removeItem: async (itemId: string) => {
    set({ isLoading: true, error: null });
    try {
      await cartService.removeItem(itemId);
      // Refresh cart after removing
      await get().fetchCart();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to remove cart item';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  clearCart: async () => {
    set({ isLoading: true, error: null });
    try {
      await cartService.clearCart();
      // Clear local cart state
      set({ cart: null, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to clear cart';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  setCart: (cart: Cart | null) => {
    set({ cart });
  },

  clearError: () => {
    set({ error: null });
  },
}));
